import { makeAutoObservable, observable } from "mobx";
import { ChatMessage } from "../../molecules/ChatMessageBox";
import { DEFAULT_SCOPE, Scope, ScopeKey, toScopeKey } from "../common";

/**
 * MobX store for agent drawer chat messages.
 * Any component/hook within the provider tree can call store.add()
 * to post messages — ChatMessageBox just observes store.messages.
 */
export class AgentChatStore {
  messages = observable.array<ChatMessage>([]);
  private _nextId = 0;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** Add a message. Loading messages are always kept at the bottom. */
  add(msg: Omit<ChatMessage, "id">) {
    const newMsg: ChatMessage = { ...msg, id: String(++this._nextId) };
    if (newMsg.isLoading) {
      // Loading indicators always go at the very end
      this.messages.push(newMsg);
    } else {
      // Insert before the first loading indicator so chronological order is preserved
      const firstLoadingIdx = this.messages.findIndex((m) => m.isLoading);
      if (firstLoadingIdx === -1) {
        this.messages.push(newMsg);
      } else {
        this.messages.spliceWithArray(firstLoadingIdx, 0, [newMsg]);
      }
    }
  }

  /** Remove all loading-indicator messages. */
  removeLoading() {
    this.messages.replace(this.messages.filter((m) => !m.isLoading));
  }

  /** Clear all messages and reset the ID counter. */
  clear() {
    this.messages.clear();
    this._nextId = 0;
  }

  get hasMessages(): boolean {
    return this.messages.length > 0;
  }
}

/**
 * Registry to manage AgentChatStore instances by scope (e.g. patient tab).
 * The provider holds one registry instance; hooks resolve the inner store for their active scope.
 * Stores are automatically created on demand and can be cleared when a scope is closed.
 */
export class ScopedAgentChatStoreRegistry {
  /** Map of scope key → AgentChatStore instance */
  stores = new Map<ScopeKey, AgentChatStore>();

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /** Get or create the store for a scope. */
  getStore(scope: Scope): AgentChatStore {
    const key = toScopeKey(scope);
    let store = this.stores.get(key);
    if (!store) {
      store = new AgentChatStore();
      this.stores.set(key, store);
    }
    return store;
  }

  /** Get or create the shared fallback store when no explicit scope is provided. */
  getDefaultStore(): AgentChatStore {
    return this.getStore(DEFAULT_SCOPE);
  }

  /** Clear and remove the store for a scope (e.g. patient tab closed). */
  clearScope(scope: Scope) {
    const key = toScopeKey(scope);
    const store = this.stores.get(key);
    if (store) {
      store.clear();
      this.stores.delete(key);
    }
  }

  /** Clear and remove the fallback default scope store. */
  clearDefaultScope() {
    this.clearScope(DEFAULT_SCOPE);
  }
}
